import { RacemapColors } from '@racemap/utilities/consts/common';
import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import type {
  GeoObjects,
  PointObject,
  PointOnLineStringObject,
} from '@racemap/utilities/types/geos';
import type { Point } from '@racemap/utilities/types/types';
import type { Feature, FeatureCollection, Point as GeojsonPoint, Position } from 'geojson';
import type { DataDrivenPropertyValueSpecification, Map as MapboxMap } from 'mapbox-gl';
import { type FC, useContext, useEffect, useMemo, useRef } from 'react';
import { Layer, type MapMouseEvent, Source } from 'react-map-gl/mapbox';
import { MapContext, type NewDataProps } from '../BasicMap/BasicMap';
import { type AUTOFOCUS, convertPointsToFeatures, injectFeatureIds } from '../helpers';
import { useMapImage } from '../helpers/useMapImage';

export type PointProperties = Record<string, unknown> & {
  lineWidth?: number;
  color?: string;
};

export type PointLayerPoints =
  | null
  | Position
  | Array<Position>
  | Feature<GeojsonPoint, PointProperties>
  | Array<Feature<GeojsonPoint, PointProperties> | PointObject | PointOnLineStringObject>
  | FeatureCollection<GeojsonPoint, PointProperties>
  | PointOnLineStringObject
  | PointObject
  | GeoObjects
  | Array<Point>;

export interface Props {
  points: PointLayerPoints;
  color?: string;
  fillColor?: string;
  lineWidth?: number;
  /** A plain number, or a mapbox expression to size circles from feature properties. */
  radius?: DataDrivenPropertyValueSpecification<number>;
  /** 0 is a sharp circle, 1 fades it from the center out — a glow. */
  blur?: number;
  /** A diamond needs a plain number `radius` and ignores `blur`. */
  shape?: 'circle' | 'diamond';
  beforeId?: string;
  visible?: boolean;
  onNewData?: (props: NewDataProps) => void;
  onMouseEnter?: (evt: MapMouseEvent) => void;
  onMouseLeave?: (evt: MapMouseEvent) => void;
  onClick?: (evt: MapMouseEvent) => void;
  autofocus?: boolean | AUTOFOCUS;
  id?: string;
}

export const PointLayer: FC<Props> = ({
  points,
  color = RacemapColors.PaleBlue,
  fillColor = RacemapColors.CloudBlue,
  lineWidth = 2,
  radius = 2,
  blur,
  shape = 'circle',
  beforeId,
  visible = true,
  onNewData,
  autofocus = false,
  onMouseEnter,
  onMouseLeave,
  onClick,
  id,
}) => {
  const idRef = useRef(id || shortIdBuilder());
  const { features, paint } = useMemo(() => {
    const converted = convertPointsToFeatures(points, color, lineWidth, fillColor, radius);
    if (blur == null) return converted;
    return { ...converted, paint: { ...converted.paint, 'circle-blur': blur } };
  }, [points, color, lineWidth, fillColor, radius, blur]);
  const diamondImageId = useDiamondImage(
    shape === 'diamond' ? { color, fillColor, lineWidth, radius: Number(radius) } : null,
  );
  const featuresWithIdProperty = useMemo(() => injectFeatureIds(features), [features]);
  const featureCollection: FeatureCollection<GeojsonPoint, PointProperties> = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: [...featuresWithIdProperty],
    }),
    [featuresWithIdProperty],
  );
  const {
    setMouseEnterAction,
    setMouseLeaveAction,
    setOnClickAction,
    addInteractiveLayerIds,
    mapStyle,
  } = useContext(MapContext);
  const layers = mapStyle?.layers.map((l) => l.id) || [];

  useEffect(() => {
    addInteractiveLayerIds(`layer-point-${idRef.current}`);
  }, []);

  useEffect(() => {
    onNewData?.({ layer: `layer-point-${idRef.current}`, data: featureCollection, autofocus });
  }, [featureCollection]);

  useEffect(() => {
    if (onMouseEnter != null) {
      setMouseEnterAction(`point-${idRef.current}`, onMouseEnter);
    }
    if (onMouseLeave != null) {
      setMouseLeaveAction(`point-${idRef.current}`, onMouseLeave);
    }
    if (onClick != null) {
      setOnClickAction(`point-${idRef.current}`, onClick);
    }
  }, [onMouseEnter, onMouseLeave, onClick]);

  if (!visible) return null;
  if (shape === 'diamond' && diamondImageId == null) return null;

  return (
    <Source
      type="geojson"
      data={featureCollection}
      id={`point-${idRef.current}`}
      key={`point-${idRef.current}`}
      promoteId={'_id'}
    >
      {diamondImageId != null ? (
        <Layer
          id={`layer-point-${idRef.current}`}
          key={`layer-point-${idRef.current}`}
          type="symbol"
          layout={{
            'icon-image': diamondImageId,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          }}
          beforeId={beforeId != null && layers.includes(beforeId) ? beforeId : undefined}
        />
      ) : (
        <Layer
          id={`layer-point-${idRef.current}`}
          key={`layer-point-${idRef.current}`}
          type="circle"
          paint={paint}
          beforeId={beforeId != null && layers.includes(beforeId) ? beforeId : undefined}
        />
      )}
    </Source>
  );
};

type DiamondStyle = { color: string; fillColor: string; lineWidth: number; radius: number };

// Circle layers only draw circles, so a diamond is a symbol layer over an image drawn once per style.
function useDiamondImage(style: DiamondStyle | null): string | null {
  const imageId =
    style == null
      ? null
      : `diamond-${style.color}-${style.fillColor}-${style.lineWidth}-${style.radius}`;
  return useMapImage(imageId, (map, id) => {
    if (style != null) addDiamondImage(map, id, style);
  });
}

function addDiamondImage(map: MapboxMap, imageId: string, style: DiamondStyle): void {
  const pixelRatio = window.devicePixelRatio || 1;
  const half = (style.radius + style.lineWidth) * pixelRatio;
  const size = Math.ceil(2 * half);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (context == null) return;

  const inner = style.radius * pixelRatio;
  context.beginPath();
  context.moveTo(half, half - inner);
  context.lineTo(half + inner, half);
  context.lineTo(half, half + inner);
  context.lineTo(half - inner, half);
  context.closePath();
  context.fillStyle = style.fillColor;
  context.fill();
  context.lineWidth = style.lineWidth * pixelRatio;
  context.strokeStyle = style.color;
  context.stroke();

  map.addImage(imageId, context.getImageData(0, 0, size, size), { pixelRatio });
}
